import { useState, useEffect } from 'react';
import { Outlet, Link, Navigate, useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  CalendarIcon,
  ChartPieIcon,
  Cog6ToothIcon,
  ClipboardDocumentListIcon,
  CubeTransparentIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  HomeIcon,
  NumberedListIcon,
  TableCellsIcon,
  TruckIcon,
  UserGroupIcon,
  WrenchIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ArchiveBoxIcon, BoltIcon, BookOpenIcon, ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon, WrenchScrewdriverIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import ACALogo from '../assets/acaStackLogo.png';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function Layout() {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(['Jobs']); // Jobs expanded by default
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [userRole, setUserRole] = useState(null);

  // Fetch user profile data and role on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('users')
          .select('first_name, last_name, email, role')
          .eq('id', user.id) // Now both IDs are UUIDs
          .single();
        if (error) throw error;
        if (data) {
          setFirstName(data.first_name || '');
          setLastName(data.last_name || '');
          setEmail(data.email || user.email);
          setUserRole(data.role || '');
        }
      } catch (err) {
        console.error('Error fetching user profile:', err.message);
        console.log('Failed to fetch user profile.');
      }
    };

    fetchUserProfile();
  }, [user]);

  // Function to update user profile
  const handleUpdateProfile = async () => {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          first_name: firstName,
          last_name: lastName,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id); // Now both IDs are UUIDs
      if (error) throw error;
      setIsProfileModalOpen(false);
      console.log('Profile updated successfully!');
    } catch (err) {
      console.error('Error updating profile:', err.message);
      console.log('Failed to update profile.');
    }
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, current: location.pathname === '/dashboard' },
    { name: 'Calendar', href: '/calendar', icon: CalendarIcon, current: location.pathname === '/calendar' },
    { name: 'Reports', href: '/reports', icon: TableCellsIcon, current: location.pathname === '/reports' },
    {
      name: 'Jobs',
      icon: FolderIcon,
      isGroup: true,
      children: [
        { name: 'All Jobs', href: '/jobs', current: location.pathname === '/jobs' },
        { name: 'Standard POs', href: '/standard-pos', current: location.pathname === '/standard-pos' },
        { name: 'Kanban POs', href: '/kanban', current: location.pathname === '/kanban' },
      ]
    },
    {
      name: 'Inventory',
      icon: CubeTransparentIcon,
      isGroup: true,
      children: [
        { name: 'Parts', href: '/parts', current: location.pathname === '/parts' },
      ]
    },
    {
      name: 'Operations',
      icon: WrenchIcon,
      isGroup: true,
      children: [
        { name: 'Production', href: '/production', current: location.pathname === '/production' },
        { name: 'Machines', href: '/machines', current: location.pathname === '/machines' },
        { name: 'Deliveries', href: '/deliveries', current: location.pathname === '/deliveries' },
      ]
    },
    {
      name: 'Sales',
      icon: UserGroupIcon,
      isGroup: true,
      children: [
        { name: 'Customers', href: '/customers', current: location.pathname === '/customers' },
        { name: 'Customer Quotes', href: '/customer-quotes', current: location.pathname.startsWith('/customer-quotes') },
        { name: 'Freeform Quotes', href: '/freeform-quotes', current: location.pathname.startsWith('/freeform-quotes') },
      ]
    },
    {
      name: 'Purchasing',
      icon: ClipboardDocumentListIcon,
      isGroup: true,
      children: [
        { name: 'Vendor POs', href: '/purchase-orders', current: location.pathname.startsWith('/purchase-orders') },
        { name: 'Vendors', href: '/vendors', current: location.pathname === '/vendors' },
      ]
    },
    ...(userRole === 'admin' ? [
      {
        name: 'Finance',
        icon: ChartPieIcon,
        isGroup: true,
        children: [
          { name: 'Financials', href: '/financials', current: location.pathname === '/financials' },
          { name: 'Invoices', href: '/invoices', current: location.pathname === '/invoices' },
        ]
      },
    ] : []),
  ];

  const userNavigation = [
    { name: 'Your profile', href: '/settings' },
    { name: 'Sign out', onClick: signOut },
  ];

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
              <div className="flex h-16 shrink-0 items-center">
              <img
                alt="Machine Shop"
                src={ACALogo}
                className="h-11 object-cover rounded-md"
              />
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2 space-y-1">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          {item.isGroup ? (
                            <>
                              <button
                                onClick={() => toggleGroup(item.name)}
                                className={classNames(
                                  item.children?.some(child => child.current)
                                    ? 'bg-gray-50 text-indigo-600'
                                    : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                                  'group flex w-full gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                                )}
                              >
                                <item.icon
                                  aria-hidden="true"
                                  className={classNames(
                                    item.children?.some(child => child.current) ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                                    'size-6 shrink-0',
                                  )}
                                />
                                <span className="flex-1 text-left">{item.name}</span>
                                <ChevronRightIcon
                                  className={classNames(
                                    'size-4 text-gray-400 transition-transform',
                                    expandedGroups.includes(item.name) ? 'rotate-90' : ''
                                  )}
                                />
                              </button>
                              {expandedGroups.includes(item.name) && (
                                <ul className="mt-1 space-y-1">
                                  {item.children.map((child) => (
                                    <li key={child.name}>
                                      <Link
                                        to={child.href}
                                        onClick={() => setSidebarOpen(false)}
                                        className={classNames(
                                          child.current
                                            ? 'bg-gray-50 text-indigo-600'
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-indigo-600',
                                          'group flex gap-x-3 rounded-md pl-11 pr-2 py-2 text-sm/6 font-semibold',
                                        )}
                                      >
                                        {child.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </>
                          ) : (
                            <Link
                              to={item.href}
                              onClick={() => setSidebarOpen(false)}
                              className={classNames(
                                item.current
                                  ? 'bg-gray-50 text-indigo-600'
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                                'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                              )}
                            >
                              <item.icon
                                aria-hidden="true"
                                className={classNames(
                                  item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                                  'size-6 shrink-0',
                                )}
                              />
                              {item.name}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="mt-auto gap-2">
                    <div className="border-t border-gray-200 pt-4">
                      <button
                        onClick={() => setIsProfileModalOpen(true)}
                        className="w-full flex items-center gap-x-3 border-2 rounded-md p-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-indigo-600"
                      >
                        <Cog6ToothIcon className="size-6 text-gray-400 group-hover:text-indigo-600" aria-hidden="true" />
                        My Profile
                      </button>
                      <button
                        onClick={signOut}
                        className="w-full flex items-center justify-center rounded-md p-2 mt-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-400"
                      >
                        Logout
                      </button>
                    </div>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Static sidebar for desktop with collapsible functionality */}
      <div
        className={classNames(
          'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col border-r border-slate-200 bg-white transition-all duration-200',
          sidebarCollapsed ? 'lg:w-14' : 'lg:w-48',
        )}
      >
        {/* Header area with a collapse/expand button */}
        <div className="flex h-12 shrink-0 items-center px-3 border-b border-slate-100">
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-sm transition-colors"
          >
            {sidebarCollapsed ? (
              <ChevronDownIcon className="w-4 h-4 -rotate-90" aria-hidden="true" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 rotate-90" aria-hidden="true" />
            )}
            <span className="sr-only">Toggle sidebar</span>
          </button>
          {!sidebarCollapsed && (
            <span className="ml-2 text-sm font-semibold text-slate-700">Machine Shop</span>
          )}
        </div>

        {/* Nav Items */}
        <div className="flex grow flex-col overflow-y-auto px-2 py-2">
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-0.5">
              {navigation.map((item) => (
                <li key={item.name}>
                  {item.isGroup ? (
                    <>
                      {sidebarCollapsed ? (
                        <Link
                          to={item.children?.[0]?.href || '#'}
                          className={classNames(
                            item.children?.some(child => child.current)
                              ? 'bg-slate-100 text-slate-900'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                            'group flex items-center w-full rounded-sm px-2 py-1.5 text-sm font-medium transition-colors',
                          )}
                        >
                          <item.icon
                            aria-hidden="true"
                            className={classNames(
                              item.children?.some(child => child.current) ? 'text-slate-700' : 'text-slate-400 group-hover:text-slate-600',
                              'size-4 shrink-0',
                            )}
                          />
                        </Link>
                      ) : (
                        <button
                          onClick={() => toggleGroup(item.name)}
                          className={classNames(
                            item.children?.some(child => child.current)
                              ? 'bg-slate-100 text-slate-900'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                            'group flex items-center w-full rounded-sm px-2 py-1.5 text-sm font-medium transition-colors',
                          )}
                        >
                        <item.icon
                          aria-hidden="true"
                          className={classNames(
                            item.children?.some(child => child.current) ? 'text-slate-700' : 'text-slate-400 group-hover:text-slate-600',
                            'size-4 shrink-0',
                          )}
                        />
                        {!sidebarCollapsed && (
                          <>
                            <span className="ml-2.5 flex-1 text-left">{item.name}</span>
                            <ChevronRightIcon
                              className={classNames(
                                'size-3 text-slate-400 transition-transform',
                                expandedGroups.includes(item.name) ? 'rotate-90' : ''
                              )}
                            />
                          </>
                        )}
                      </button>
                      )}
                      {!sidebarCollapsed && expandedGroups.includes(item.name) && (
                        <ul className="mt-0.5 space-y-0.5 ml-2 border-l border-slate-200">
                          {item.children.map((child) => (
                            <li key={child.name}>
                              <Link
                                to={child.href}
                                className={classNames(
                                  child.current
                                    ? 'bg-slate-100/80 text-slate-900 border-l-2 border-slate-900 -ml-px'
                                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800 border-l-2 border-transparent -ml-px',
                                  'group flex items-center pl-5 pr-2 py-1.5 text-sm font-medium transition-colors',
                                )}
                              >
                                {child.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={classNames(
                        item.current
                          ? 'bg-slate-100/80 text-slate-900 border-l-2 border-slate-900'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-2 border-transparent',
                        'group flex items-center rounded-r-sm pl-2 pr-2 py-1.5 text-sm font-medium transition-colors -ml-0.5',
                      )}
                    >
                      <item.icon
                        aria-hidden="true"
                        className={classNames(
                          item.current ? 'text-slate-700' : 'text-slate-400 group-hover:text-slate-600',
                          'size-4 shrink-0',
                        )}
                      />
                      {!sidebarCollapsed && <span className="ml-2.5">{item.name}</span>}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>
          {/* Footer (Profile & Logout) */}
          <div className="mt-auto border-t border-slate-100 pt-2">
            <button
              onClick={() => setIsProfileModalOpen(true)}
              className={classNames(
                'flex items-center w-full rounded-sm px-2 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors',
                sidebarCollapsed ? 'justify-center' : ''
              )}
            >
              <Cog6ToothIcon className="size-4 text-slate-400" aria-hidden="true" />
              {!sidebarCollapsed && <span className="ml-2.5">Settings</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className={classNames('transition-all duration-200', sidebarCollapsed ? 'lg:pl-14' : 'lg:pl-48')}>
        {/* Mobile Header with Menu Icon */}
        <div className="lg:hidden sticky top-0 z-40 flex h-12 shrink-0 items-center gap-x-4 border-b border-slate-200 bg-white px-4">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-slate-600"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-5" />
          </button>
          <div className="flex-1 text-sm font-semibold text-slate-800">
            {(() => {
              for (const item of navigation) {
                if (item.isGroup) {
                  const currentChild = item.children?.find(child => child.current);
                  if (currentChild) return currentChild.name;
                } else if (item.current) {
                  return item.name;
                }
              }
              return 'Machine Shop';
            })()}
          </div>
        </div>

        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>

      {/* Profile Modal */}
      <Dialog open={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            transition
            className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all duration-300 ease-in-out data-[closed]:scale-95 data-[closed]:opacity-0"
          >
            <h2 className="text-xl font-semibold mb-4 text-gray-900">My Profile</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
                  placeholder="Enter your last name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                  placeholder="Your email"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => setIsProfileModalOpen(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateProfile}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm"
              >
                Save
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}

export default Layout;